/**
 * Signs a student out when they leave the screen, under their tenant's rules.
 *
 * Two triggers, deliberately kept apart because one number cannot serve both:
 *
 *   AWAY  the tab is hidden or the window has lost focus. Ten seconds is reasonable —
 *         nobody reads a lesson from another application. There is no warning, because
 *         a hidden tab cannot show one; they are told why on the sign-in page instead.
 *   IDLE  the page is in front of them but untouched. A warning counts down first and
 *         any movement cancels it, so a student thinking about a question is not thrown
 *         out without a chance to stay.
 *
 * Away time is measured from a TIMESTAMP, not a timer. Browsers throttle timers in
 * hidden tabs — a setTimeout(10s) can fire a minute late or not at all — so the elapsed
 * time is recalculated the moment the tab comes back. The timer is only there to catch
 * a window that is blurred but still visible, where throttling does not apply.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Anything a person does that proves they are still there. */
const ACTIVITY = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'pointerdown', 'wheel'];

export const AWAY_REASON = 'away';
export const IDLE_REASON = 'idle';
// Not this hook's doing: the server signed this device out because the account reached
// its device limit somewhere else, or somebody cleared its sessions. It lives here so
// the sign-in page has one place to look up why anybody was signed out.
export const ELSEWHERE_REASON = 'signed_in_elsewhere';

export default function useAwayGuard({ policy, active = true, onExpire }) {
  const enabled = !!(active && policy?.enabled);
  const awaySeconds = Math.max(3, Number(policy?.away_seconds) || 10);
  const idleSeconds = Math.max(10, Number(policy?.idle_seconds) || 300);
  const warnSeconds = Math.max(5, Number(policy?.idle_warn_seconds) || 30);
  const awayOn = enabled && policy?.away_enabled !== false;
  const idleOn = enabled && !!policy?.idle_enabled;

  // Countdown shown to the student while the idle timer is about to run out.
  const [warning, setWarning] = useState(null);     // seconds left, or null

  const firedRef = useRef(false);
  const leftAtRef = useRef(null);
  const awayTimer = useRef(null);
  const idleTimer = useRef(null);
  const tickTimer = useRef(null);
  const onExpireRef = useRef(onExpire);
  const resetIdleRef = useRef(null);
  useEffect(() => { onExpireRef.current = onExpire; }, [onExpire]);

  const fire = useCallback((reason, secondsAway) => {
    if (firedRef.current) return;
    firedRef.current = true;
    setWarning(null);
    try { onExpireRef.current?.({ reason, seconds: Math.round(secondsAway) }); }
    catch { /* the caller's problem, not ours */ }
  }, []);

  const clearAll = useCallback(() => {
    [awayTimer, idleTimer, tickTimer].forEach((t) => {
      if (t.current) { clearTimeout(t.current); clearInterval(t.current); t.current = null; }
    });
  }, []);

  /** Restart the idle countdown. Called on every scrap of activity. */
  const resetIdle = useCallback(() => {
    if (!idleOn || firedRef.current) return;
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (tickTimer.current) { clearInterval(tickTimer.current); tickTimer.current = null; }
    setWarning(null);
    idleTimer.current = setTimeout(() => {
      // Start the visible countdown rather than signing them out on the spot.
      let left = warnSeconds;
      setWarning(left);
      tickTimer.current = setInterval(() => {
        left -= 1;
        if (left <= 0) {
          clearInterval(tickTimer.current);
          tickTimer.current = null;
          fire(IDLE_REASON, idleSeconds + warnSeconds);
        } else {
          setWarning(left);
        }
      }, 1000);
    }, Math.max(0, (idleSeconds - warnSeconds) * 1000));
  }, [idleOn, idleSeconds, warnSeconds, fire]);
  useEffect(() => { resetIdleRef.current = resetIdle; }, [resetIdle]);

  /* ---------------------------------------------------------------- away
   *
   * Everything here works off a TIMESTAMP held in a ref, never off elapsed timer state.
   *
   * Two reasons. A hidden tab's timers are throttled, so a setTimeout(10s) may fire a
   * minute late or never — only the clock can be trusted. And this effect re-runs
   * whenever its dependencies change: the exam screen re-renders every second for its
   * countdown, and an earlier version cleared the away start time on every teardown, so
   * the elapsed time reset each tick and the guard never fired at all. `leftAtRef` is
   * therefore cleared only when the student actually comes back.
   */
  useEffect(() => {
    if (!awayOn) return undefined;

    const checkElapsed = () => {
      const since = leftAtRef.current;
      if (!since || firedRef.current) return false;
      const seconds = (Date.now() - since) / 1000;
      if (seconds >= awaySeconds) { fire(AWAY_REASON, seconds); return true; }
      return false;
    };

    const left = () => {
      if (firedRef.current || leftAtRef.current) return;
      leftAtRef.current = Date.now();
      // Belt and braces: a timer for the blurred-but-visible case (not throttled), and a
      // poll in case the browser throttles that one too.
      awayTimer.current = setInterval(checkElapsed, 1000);
    };

    const back = () => {
      if (awayTimer.current) { clearInterval(awayTimer.current); awayTimer.current = null; }
      // The authoritative check happens HERE, on return, where the clock is reliable.
      const expired = checkElapsed();
      leftAtRef.current = null;
      if (!expired && !firedRef.current) resetIdleRef.current?.();
    };

    const onVisibility = () => (document.visibilityState === 'hidden' ? left() : back());
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', left);
    window.addEventListener('focus', back);
    // Already away when the guard mounted, or when it re-ran mid-absence.
    if (document.visibilityState === 'hidden' || !document.hasFocus()) left();

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', left);
      window.removeEventListener('focus', back);
      if (awayTimer.current) { clearInterval(awayTimer.current); awayTimer.current = null; }
      // leftAtRef is deliberately NOT cleared — see the note above.
    };
  }, [awayOn, awaySeconds, fire]);

  /* ---------------------------------------------------------------- idle */
  useEffect(() => {
    if (!idleOn) { setWarning(null); return undefined; }
    const bump = () => resetIdle();
    ACTIVITY.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    resetIdle();
    return () => {
      ACTIVITY.forEach((e) => window.removeEventListener(e, bump));
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (tickTimer.current) clearInterval(tickTimer.current);
    };
  }, [idleOn, resetIdle]);

  useEffect(() => () => clearAll(), [clearAll]);
  // A guard that switches off (policy change, leaving the exam) must forget it fired.
  useEffect(() => { if (!enabled) firedRef.current = false; }, [enabled]);

  return {
    /** Seconds left before an idle sign-out, or null when nothing is counting down. */
    warning,
    armed: enabled,
    awaySeconds,
    /** Call after any deliberate activity the DOM events would not see. */
    keepAlive: resetIdle,
  };
}
